// Tarjeta QR del cliente + escáner en el celular del empleado (23-oct-2026, «sin tablet»).
// Es el tercer modo de registrar visitas, junto al kiosco y al QR del mostrador: el CLIENTE trae su
// QR (lo guarda en su celular desde el kiosco o el QR) y el EMPLEADO lo escanea con el suyo desde el
// manager. La visita se registra al instante (el escaneo la autoriza), sin bandeja de aprobación.
// Se usa en: Programa de lealtad (compacta), Escanear tarjeta (completa) y el asistente de inicio.
// Se guarda solo (PUT /loyalty/settings { card_scan_enabled }). GRATIS.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ScanLine, Smartphone, CheckCircle2, Loader2, CreditCard } from 'lucide-react';
import { baseApi, BaseApiError } from '../../api/baseClient';
import { cardScanApi } from '../../api/cardScanApi';
import { useError } from '../../context/ErrorContext';

interface Props {
  /** 'compacta' = opción dentro de Programa de lealtad / asistente; 'completa' = cabecera de «Escanear tarjeta». */
  variante?: 'compacta' | 'completa';
  /** Solo en la completa: abre el lector. Si no se pasa, el botón lleva a la pantalla. */
  onScan?: () => void;
}

export const CARD_SCAN_EVENT = 'lealify:card-scan-changed';

const CardScanCard: React.FC<Props> = ({ variante = 'completa', onScan }) => {
  const { addError } = useError();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    baseApi.get<{ settings?: { card_scan_enabled?: number } }>('/loyalty/settings')
      .then((r) => { if (alive) setEnabled(Number(r?.settings?.card_scan_enabled) === 1); })
      .catch(() => { if (alive) setEnabled(false); });
    return () => { alive = false; };
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true);
    const prev = enabled;
    setEnabled(next);
    try {
      await cardScanApi.setEnabled(next);
      window.dispatchEvent(new Event(CARD_SCAN_EVENT));   // el menú y el historial del cliente se enteran
    } catch (error) {
      setEnabled(prev);
      const detail = error instanceof BaseApiError ? error.message : '';
      addError(detail ? `No se pudo guardar: ${detail}` : 'No se pudo cambiar la tarjeta QR.', 'unknown', 'card-scan-toggle');
    } finally {
      setSaving(false);
    }
  };

  const interruptor = (
    <button
      type="button"
      role="switch"
      aria-checked={!!enabled}
      disabled={enabled === null || saving}
      onClick={() => toggle(!enabled)}
      className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? 'bg-[#0057B8]' : 'bg-slate-300 dark:bg-slate-600'}`}
    >
      <span className="sr-only">{enabled ? 'Desactivar tarjeta QR' : 'Activar tarjeta QR'}</span>
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  if (variante === 'compacta') {
    return (
      <div className={`rounded-xl border-2 p-4 transition-colors ${enabled ? 'border-[#0057B8] bg-[#0057B8]/5' : 'border-line bg-surface'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#0057B8]/10 text-[#0057B8] dark:text-blue-300"><ScanLine className="h-5 w-5" /></span>
            <div>
              <p className="font-semibold text-content flex items-center gap-2">
                Tarjeta QR del cliente
                <span className="text-[10px] font-bold uppercase tracking-wide bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300 px-1.5 py-0.5 rounded">Gratis</span>
              </p>
              <p className="text-xs text-content-3 mt-0.5">Tu cliente guarda su tarjeta en el celular y tú la escaneas con el tuyo. Sin tablet.</p>
            </div>
          </div>
          {saving ? <Loader2 className="h-5 w-5 animate-spin text-content-3" /> : interruptor}
        </div>
        {enabled && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <RouterLink to="/dashboard/card-scan"
              className="inline-flex items-center gap-2 rounded-full bg-[#0057B8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004494] transition-colors">
              <ScanLine className="h-4 w-4" /> Escanear tarjeta
            </RouterLink>
            <RouterLink to="/dashboard/card-scan" className="text-sm font-medium text-accent hover:underline">¿Cómo le doy su tarjeta a un cliente? →</RouterLink>
          </div>
        )}
      </div>
    );
  }

  // Completa: cabecera de «Escanear tarjeta». Apagado = invitación con los 3 pasos; prendido = el botón grande.
  const pasos: [React.ElementType, string][] = [
    [CreditCard, 'Tu cliente abre su tarjeta en el celular'],
    [Smartphone, 'Tú la escaneas con el tuyo'],
    [CheckCircle2, 'La visita se registra al instante'],
  ];
  return (
    <div className="rounded-2xl bg-[#0E1E3A] text-white overflow-hidden">
      <div className="flex flex-col lg:flex-row gap-6 p-6 lg:p-8 lg:items-center">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl lg:text-2xl font-display font-extrabold tracking-tight">
              {enabled ? 'Tu escáner está activo' : 'Escanea la tarjeta de tu cliente con tu celular'}
            </h2>
            <span className="text-[10px] font-bold uppercase tracking-wide bg-green-400/20 text-green-300 px-2 py-0.5 rounded">Gratis</span>
          </div>
          <p className="mt-2 text-sm text-white/75 max-w-2xl">
            {enabled
              ? 'Cada cliente tiene un QR propio. Cuando pague, escanéalo desde aquí y su visita queda registrada al momento, sin tablet y sin teclear números.'
              : 'Sin tablet en el mostrador: cada cliente guarda una tarjeta con su QR en el celular y, al pagar, tú la escaneas con el tuyo. La visita se registra al instante.'}
          </p>
          <ol className="mt-5 grid gap-3 sm:grid-cols-3 text-sm">
            {pasos.map(([Icon, t], i) => (
              <li key={i} className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5">
                <Icon className="h-5 w-5 text-blue-300 flex-shrink-0" /> <span>{t}</span>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {enabled ? (
              <button type="button" onClick={onScan}
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-bold text-[#0E1E3A] hover:bg-blue-50 transition-colors shadow-lg">
                <ScanLine className="h-5 w-5" /> Escanear tarjeta
              </button>
            ) : (
              <button type="button" disabled={enabled === null || saving} onClick={() => toggle(true)}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#0E1E3A] hover:bg-blue-50 transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />} Activar el escáner
              </button>
            )}
            {enabled && (
              <span className="flex items-center gap-2 text-sm text-white/75">{interruptor} Activado</span>
            )}
          </div>
        </div>
        {/* Maqueta de la tarjeta, para que el dueño vea qué recibe su cliente */}
        <div className="self-center flex-shrink-0">
          <div className="w-[150px] rounded-[22px] bg-gradient-to-br from-[#0057B8] to-[#1e3a8a] p-3 shadow-2xl ring-1 ring-white/20">
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/70">Mi tarjeta</p>
            <p className="text-[11px] font-semibold text-white truncate">Tu negocio</p>
            <div className="mt-2 rounded-xl bg-white p-2 grid grid-cols-5 gap-[2px]">
              {Array.from({ length: 25 }).map((_, i) => (
                <span key={i} className={`aspect-square rounded-[1px] ${[0, 1, 2, 4, 5, 7, 9, 10, 12, 14, 15, 17, 19, 20, 22, 24].includes(i) ? 'bg-[#0E1E3A]' : 'bg-white'}`} />
              ))}
            </div>
            <p className="mt-2 text-[9px] text-white/70">Ana · <span className="font-bold text-white">7 pts</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardScanCard;
